import React, { useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
} from '@mui/material';
import { dateFormate, convertToReadableFormat } from '../../../utils/helper.jsx';

type Assignment = {
  assigned_to: string;
  assigned_to_email?: string;
  assigned_by: string;
  assigned_by_email?: string;
  assigned_at: string; // ISO
  note?: string;
  status?: 'assigned' | 'reassigned' | 'unassigned';
};

interface Props {
  open: boolean;
  onClose: () => void;
  lead?: any;
  assignments?: Assignment[];
}

const statusColor: Record<NonNullable<Assignment['status']>, 'success' | 'warning' | 'default'> = {
  assigned: 'success',
  reassigned: 'warning',
  unassigned: 'default',
};

const AssignmentLogsModal: React.FC<Props> = ({ open, onClose, lead, assignments = [] }) => {
  const rows = useMemo(() => assignments, [assignments]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Assignment Logs {lead?.lead_id ? `· ${convertToReadableFormat(lead.lead_id.toString())}` : ''}
      </DialogTitle>
      <DialogContent dividers>
        {rows.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No assignment activity found.
          </Typography>
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Status</TableCell>
                  <TableCell>Assigned To</TableCell>
                  <TableCell>Assigned By</TableCell>
                  <TableCell>Date/Time</TableCell>
                  <TableCell>Note</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r, idx) => (
                  <TableRow key={idx}>
                    <TableCell>
                      <Chip
                        size="small"
                        label={convertToReadableFormat(r.status || 'assigned')}
                        color={statusColor[r.status || 'assigned']}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {convertToReadableFormat(r.assigned_to || '-')}
                      </Typography>
                      {r.assigned_to_email && (
                        <Typography variant="caption" color="text.secondary">
                          {r.assigned_to_email}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {convertToReadableFormat(r.assigned_by || '-')}
                      </Typography>
                      {r.assigned_by_email && (
                        <Typography variant="caption" color="text.secondary">
                          {r.assigned_by_email}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {r.assigned_at ? dateFormate(r.assigned_at, 'DD-MM-YYYY HH:mm') : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>{r.note || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssignmentLogsModal;
